import {
  average,
  calcProjectedGc,
  calcProjectedSales,
  toNumber,
} from './billToOrderCalc';

const STORAGE_KEY = 'groversweets_bill_to_daily_sales';

export const EMPTY_DAILY_SALE = {
  billNo: '',
  billDate: '',
  billType: '',
  itemCode: '',
  category: '',
  subCategory: '',
  itemName: '',
  quantity: '',
  taxAmount: '',
  taxRate: '',
  discountAmount: '',
  grossAmount: '',
  salesAmount: '',
  rate: '',
  outlet: '',
  cashier: '',
  punchTime: '',
  unit: '',
};

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function loadDailySales() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function saveDailySales(rows) {
  const list = Array.isArray(rows) ? rows : [];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  return list;
}

export function parseCsvText(text) {
  const table = parseCsvRows(String(text || '').replace(/^\uFEFF/, ''));
  if (table.length < 2) return [];

  const headers = table[0].map(normalizeCsvHeader);
  const rows = [];

  for (let i = 1; i < table.length; i += 1) {
    const values = table[i];
    const raw = {};
    headers.forEach((header, index) => {
      if (!header) return;
      raw[header] = values[index] ?? '';
    });
    const mapped = mapCsvRow(raw);
    if (mapped.itemCode || mapped.itemName) rows.push(mapped);
  }

  return rows;
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      if (row.some((value) => String(value).trim() !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }

  if (cell !== '' || row.length) {
    row.push(cell);
    if (row.some((value) => String(value).trim() !== '')) rows.push(row);
  }

  return rows;
}

function normalizeCsvHeader(header) {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function pick(raw, ...keys) {
  for (const key of keys) {
    const value = raw[key];
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return '';
}

function mapCsvRow(raw) {
  return {
    id: createRowId(),
    billNo: pick(raw, 'bill_no', 'billno', 'bill_number'),
    billDate: pick(raw, 'bill_date', 'date', 'sale_date', 'sales_date'),
    billType: pick(raw, 'bill_type', 'type'),
    itemCode: pick(raw, 'item_code', 'code', 'product_code'),
    category: pick(raw, 'category', 'product_category', 'department'),
    subCategory: pick(raw, 'subcategory', 'sub_category'),
    itemName: pick(raw, 'itemname', 'item_name', 'name', 'product_name'),
    quantity: pick(raw, 'qtysold', 'qty_sold', 'quantity', 'qty', 'sold_qty', 'totqty', 'tot_qty'),
    taxAmount: pick(raw, 'tax_amount'),
    taxRate: pick(raw, 'tax_rate'),
    discountAmount: pick(raw, 'dis_amount', 'discount_amount'),
    grossAmount: pick(raw, 'gross_amount'),
    salesAmount: pick(raw, 'amount', 'sales_amount', 'sales', 'total_amount'),
    rate: pick(raw, 'rate'),
    outlet: pick(raw, 'company_name', 'outlet_name', 'outlet'),
    cashier: pick(raw, 'cashier'),
    punchTime: pick(raw, 'punchtime', 'punch_time'),
    unit: pick(raw, 'unitname', 'unit_name', 'unit'),
  };
}

export function listDailySalesFromApi(res) {
  if (Array.isArray(res)) return res;
  const nested = res?.data ?? res ?? [];
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(nested?.data)) return nested.data;
  if (Array.isArray(nested?.daily_sales)) return nested.daily_sales;
  if (Array.isArray(res?.daily_sales)) return res.daily_sales;
  return [];
}

export function isPreAggregatedDailySaleRow(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.preAggregated) return true;
  return (
    row.w1_qty != null ||
    row.w1Qty != null ||
    row.avg_qty != null ||
    row.avgQty != null ||
    Array.isArray(row.qty)
  );
}

export function emptyWeeklyHistory(weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  return {
    itemCode: '',
    itemName: '',
    productId: '',
    qty: Array.from({ length: weeks }, () => 0),
    revenue: Array.from({ length: weeks }, () => 0),
    upt: Array.from({ length: weeks }, () => 0),
    avgQty: 0,
    avgUpt: 0,
    totalRevenue: 0,
    hasApiUpt: false,
    fromApi: false,
  };
}

export function historyLookupKeys(row) {
  return [
    String(row?.productId || '').trim(),
    String(row?.id || '').trim(),
    String(row?.itemCode || '').trim(),
    String(row?.itemName || '').trim(),
  ].filter(Boolean);
}

export function historyForCatalogItem(weeklyHistory, item, weekCount = 3) {
  const empty = emptyWeeklyHistory(weekCount);
  if (!(weeklyHistory instanceof Map)) return empty;
  const keys = historyLookupKeys(item);
  for (const key of keys) {
    const found = weeklyHistory.get(key);
    if (found) return found;
  }
  return empty;
}

export function metricsForCatalogItem(weeklyHistory, item, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const hist = historyForCatalogItem(weeklyHistory, item, weeks);
  const qty = Array.isArray(hist.qty) ? hist.qty : Array.from({ length: weeks }, () => 0);
  const revenue = Array.isArray(hist.revenue) ? hist.revenue : Array.from({ length: weeks }, () => 0);
  const apiUpt = Array.isArray(hist.upt) ? hist.upt : [];
  const upt = Array.from({ length: weeks }, (_, index) =>
    hist.hasApiUpt ? toNumber(apiUpt[index]) : 0
  );
  return {
    qty,
    revenue,
    upt,
    avgQty: hist.avgQty != null ? toNumber(hist.avgQty) : average(qty),
    avgUpt: hist.hasApiUpt ? toNumber(hist.avgUpt) : 0,
    totalRevenue:
      hist.totalRevenue != null
        ? toNumber(hist.totalRevenue)
        : revenue.reduce((sum, value) => sum + value, 0),
    hasApiUpt: Boolean(hist.hasApiUpt),
    fromApi: Boolean(hist.fromApi),
  };
}

/** Per-SKU average weekly sales and GC from historical metrics. */
export function deriveItemActualAverages(metrics, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  if (!metrics?.fromApi) {
    return { avgSales: 0, avgGc: 0 };
  }
  const sales = [];
  const gcEstimates = [];
  for (let index = 0; index < weeks; index += 1) {
    sales.push(toNumber(metrics.revenue[index]));
    const qty = toNumber(metrics.qty[index]);
    const upt = toNumber(metrics.upt[index]);
    if (metrics.hasApiUpt && qty > 0 && upt > 0) {
      gcEstimates.push((qty * 1000) / upt);
    }
  }
  return {
    avgSales: average(sales),
    avgGc: gcEstimates.length ? average(gcEstimates) : 0,
  };
}

/** Projected sales / GC for one catalog SKU from history × growth %. */
export function projectedForCatalogItem(item, weeklyHistory, weekCount, growthPct) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const metrics = metricsForCatalogItem(weeklyHistory, item, weeks);
  const { avgSales, avgGc } = deriveItemActualAverages(metrics, weeks);
  if (!metrics.fromApi) {
    return {
      projectedSales: null,
      projectedGc: null,
      hasData: false,
      avgSales: 0,
      avgGc: 0,
    };
  }
  return {
    projectedSales: calcProjectedSales(avgSales, growthPct),
    projectedGc: calcProjectedGc(avgGc, growthPct),
    hasData: true,
    avgSales,
    avgGc,
  };
}

/** Actual W-1/W-2/W-3 sales + GC from historical SKU rows (GC from qty ÷ UPT when available). */
export function deriveActualsFromWeeklyHistory(items, weeklyHistory, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const sales = Array.from({ length: weeks }, () => 0);
  const gcEstimates = Array.from({ length: weeks }, () => []);

  (items || []).forEach((item) => {
    const metrics = metricsForCatalogItem(weeklyHistory, item, weeks);
    if (!metrics.fromApi) return;
    for (let index = 0; index < weeks; index += 1) {
      sales[index] += toNumber(metrics.revenue[index]);
      const qty = toNumber(metrics.qty[index]);
      const upt = toNumber(metrics.upt[index]);
      if (metrics.hasApiUpt && qty > 0 && upt > 0) {
        gcEstimates[index].push((qty * 1000) / upt);
      }
    }
  });

  return {
    sales,
    gc: gcEstimates.map((list) => (list.length ? average(list) : 0)),
  };
}

function weekMetricArrays(row, weeks) {
  const qty = Array.from({ length: weeks }, (_, index) =>
    toNumber(
      row?.[`w${index + 1}_qty`] ??
        row?.[`w${index + 1}Qty`] ??
        row?.[`qty${index + 1}`] ??
        row?.qty?.[index]
    )
  );
  const revenue = Array.from({ length: weeks }, (_, index) =>
    toNumber(
      row?.[`w${index + 1}_revenue`] ??
        row?.[`w${index + 1}Revenue`] ??
        row?.[`revenue${index + 1}`] ??
        row?.revenue?.[index]
    )
  );
  const upt = Array.from({ length: weeks }, (_, index) =>
    toNumber(
      row?.[`w${index + 1}_upt`] ??
        row?.[`w${index + 1}Upt`] ??
        row?.[`upt${index + 1}`] ??
        row?.upt?.[index]
    )
  );
  const hasApiUpt =
    typeof row?.hasApiUpt === 'boolean'
      ? row.hasApiUpt
      : row?.avg_upt != null ||
        row?.avgUpt != null ||
        Array.from({ length: weeks }, (_, index) =>
          row?.[`w${index + 1}_upt`] ??
            row?.[`w${index + 1}Upt`] ??
            row?.[`upt${index + 1}`]
        ).some((value) => value != null && value !== '');
  return { qty, revenue, upt, hasApiUpt };
}

export function mapApiDailySaleRow(row) {
  if (!row || typeof row !== 'object') return null;
  const product = row.product && typeof row.product === 'object' ? row.product : {};
  const productId =
    pick(row, 'product_id', 'productId', 'item_id') || pick(product, 'id');
  const itemCode =
    pick(row, 'item_code', 'itemCode', 'product_code', 'code') ||
    pick(product, 'item_code', 'itemCode', 'code');
  const itemName =
    pick(row, 'item_name', 'itemName', 'product_name', 'name') ||
    pick(product, 'item_name', 'itemName', 'name', 'product_name');
  if (!itemCode && !itemName && !productId) return null;

  if (isPreAggregatedDailySaleRow(row)) {
    const weeks = 3;
    const metrics = weekMetricArrays(row, weeks);
    const avgQty = row.avg_qty != null || row.avgQty != null
      ? toNumber(row.avg_qty ?? row.avgQty)
      : average(metrics.qty);
    const avgUpt = row.avg_upt != null || row.avgUpt != null
      ? toNumber(row.avg_upt ?? row.avgUpt)
      : average(metrics.upt);
    const totalRevenue = row.total_revenue != null || row.totalRevenue != null
      ? toNumber(row.total_revenue ?? row.totalRevenue)
      : metrics.revenue.reduce((sum, value) => sum + value, 0);
    return {
      id: String(row.id ?? productId ?? itemCode ?? ''),
      productId,
      billNo: '',
      billDate: '',
      itemCode,
      itemName,
      quantity: String(avgQty),
      salesAmount: String(totalRevenue),
      outlet: pick(row, 'outlet_name', 'location_name', 'outlet', 'company_name'),
      preAggregated: true,
      qty: metrics.qty,
      revenue: metrics.revenue,
      upt: metrics.upt,
      avgQty,
      avgUpt,
      totalRevenue,
      hasApiUpt: Boolean(metrics.hasApiUpt),
    };
  }

  return {
    id: String(row.id ?? productId ?? itemCode ?? ''),
    productId,
    billNo: pick(row, 'bill_no', 'billNo', 'bill_number'),
    billDate: pick(row, 'bill_date', 'billDate', 'date', 'sale_date', 'sales_date'),
    itemCode,
    itemName,
    quantity: pick(row, 'quantity', 'qty', 'qty_sold', 'sold_qty', 'totqty', 'tot_qty'),
    salesAmount: pick(
      row,
      'sales_amount',
      'amount',
      'gross_amount',
      'revenue',
      'total_amount',
      'net_amount'
    ),
    outlet: pick(row, 'outlet_name', 'location_name', 'outlet', 'company_name'),
  };
}

export function parseBillDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (dmy) {
    const year = dmy[3].length === 2 ? Number(`20${dmy[3]}`) : Number(dmy[3]);
    return new Date(year, Number(dmy[2]) - 1, Number(dmy[1]));
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function indexHistoryGroup(groups, group) {
  historyLookupKeys(group).forEach((key) => {
    groups.set(key, group);
  });
}

/** Group daily sales into W-1 / W-2 / W-3 qty and revenue by item. */
export function aggregateWeeklyItemHistory(rows, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const list = Array.isArray(rows) ? rows : [];
  const groups = new Map();

  if (list.some(isPreAggregatedDailySaleRow)) {
    list.forEach((row) => {
      if (!isPreAggregatedDailySaleRow(row)) return;
      const itemCode = String(row.itemCode || '').trim();
      const itemName = String(row.itemName || '').trim();
      const productId = String(row.productId || row.product_id || row.id || '').trim();
      if (!itemCode && !itemName && !productId) return;

      const metrics = weekMetricArrays(row, weeks);
      const group = {
        itemCode,
        itemName,
        productId,
        qty: metrics.qty,
        revenue: metrics.revenue,
        upt: metrics.upt,
        avgQty: row.avgQty != null ? toNumber(row.avgQty) : average(metrics.qty),
        avgUpt: row.avgUpt != null ? toNumber(row.avgUpt) : average(metrics.upt),
        totalRevenue:
          row.totalRevenue != null
            ? toNumber(row.totalRevenue)
            : metrics.revenue.reduce((sum, value) => sum + value, 0),
        hasApiUpt: Boolean(metrics.hasApiUpt),
        fromApi: true,
      };
      indexHistoryGroup(groups, group);
    });
    return groups;
  }

  const dated = withWeekIndex(list, weeks);
  dated.forEach((row) => {
    if (row._week < 0) return;

    const itemCode = String(row.itemCode || '').trim();
    const itemName = String(row.itemName || '').trim();
    const productId = String(row.productId || row.product_id || '').trim();
    const key = productId || itemCode || itemName;
    if (!key) return;

    if (!groups.has(key)) {
      const group = {
        itemCode,
        itemName,
        productId,
        qty: Array.from({ length: weeks }, () => 0),
        revenue: Array.from({ length: weeks }, () => 0),
        upt: Array.from({ length: weeks }, () => 0),
        avgQty: 0,
        avgUpt: 0,
        totalRevenue: 0,
        fromApi: true,
      };
      indexHistoryGroup(groups, group);
    }
    const group = groups.get(key);
    group.qty[row._week] += toNumber(row.quantity);
    group.revenue[row._week] += toNumber(row.salesAmount);
    if (!group.itemName && itemName) group.itemName = itemName;
    if (!group.itemCode && itemCode) group.itemCode = itemCode;
    if (!group.productId && productId) group.productId = productId;
    indexHistoryGroup(groups, group);
  });

  groups.forEach((group) => {
    group.avgQty = average(group.qty);
    group.totalRevenue = group.revenue.reduce((sum, value) => sum + value, 0);
  });

  return groups;
}

export const SALES_HISTORY_OPTIONS = [
  { value: '3', label: '3 Weeks (W1, W2, W3)' },
];

export function salesHistoryWeeksApiValue(weeks) {
  const n = Math.max(1, Number(weeks) || 0);
  if (!Number.isFinite(n) || n < 1) return '';
  const labels = Array.from({ length: n }, (_, i) => `W${i + 1}`);
  return `${n} weeks(${labels.join(', ')})`;
}

export function filterDailySales(rows, { outletName, itemKeys } = {}) {
  let list = Array.isArray(rows) ? rows : [];
  const outlet = String(outletName || '').trim().toLowerCase();
  if (outlet) {
    list = list.filter((row) => {
      const rowOutlet = String(row.outlet || '').trim().toLowerCase();
      if (!rowOutlet) return true;
      return rowOutlet === outlet;
    });
  }
  if (itemKeys instanceof Set && itemKeys.size) {
    list = list.filter((row) =>
      historyLookupKeys(row).some((key) => itemKeys.has(key))
    );
  }
  return list;
}

/** Store-level W-1 / W-2 / W-3 sales and guest-count (unique bills). */
export function aggregateWeeklyStoreTotals(rows, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const list = Array.isArray(rows) ? rows : [];

  if (list.some(isPreAggregatedDailySaleRow)) {
    const sales = Array.from({ length: weeks }, () => 0);
    list.forEach((row) => {
      if (!isPreAggregatedDailySaleRow(row)) return;
      const metrics = weekMetricArrays(row, weeks);
      metrics.revenue.forEach((value, index) => {
        sales[index] += value;
      });
    });
    return {
      sales,
      gc: Array.from({ length: weeks }, () => 0),
    };
  }

  const dated = withWeekIndex(list, weeks);
  const sales = Array.from({ length: weeks }, () => 0);
  const bills = Array.from({ length: weeks }, () => new Set());

  dated.forEach((row) => {
    if (row._week < 0) return;
    sales[row._week] += toNumber(row.salesAmount);
    const billKey = [row.billNo, row.billDate].map((v) => String(v || '').trim()).join('::');
    bills[row._week].add(billKey !== '::' ? billKey : `${row._day}-${row.itemCode || row.itemName || ''}`);
  });

  return {
    sales,
    gc: bills.map((set) => set.size),
  };
}

function formatWeekEndDate(time) {
  const d = new Date(time);
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  return `${day} ${month}`;
}

/** W-1 / W-2 / W-3 week-end dates. Chronological: oldest first. */
export function weeklyRangeLabels(rows, weekCount = 3) {
  const weeks = Math.max(1, Number(weekCount) || 3);
  const dated = withWeekIndex(rows, weeks);
  const times = dated
    .map((row) => row._day)
    .filter((day) => day != null);
  const selected = times.length ? Math.max(...times) : null;
  if (selected == null) return Array.from({ length: weeks }, () => '');
  return Array.from({ length: weeks }, (_, index) => {
    const end = selected - (weeks - index) * 7 * 86400000;
    return formatWeekEndDate(end);
  });
}

function withWeekIndex(rows, weeks) {
  const list = Array.isArray(rows) ? rows : [];
  const dated = list.map((row) => ({
    ...row,
    _date: parseBillDate(row.billDate),
  }));
  const times = dated
    .map((row) => row._date)
    .filter(Boolean)
    .map((date) => startOfDay(date));
  const latest = times.length ? Math.max(...times) : startOfDay(new Date());

  return dated.map((row) => {
    let week = -1;
    let day = null;
    if (row._date) {
      day = startOfDay(row._date);
      const diffDays = Math.floor((latest - day) / 86400000);
      week = Math.floor(diffDays / 7);
      if (week < 0 || week >= weeks) week = -1;
    }
    return { ...row, _week: week, _day: day };
  });
}
